import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import Loader from "../../ui/Loader";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import {
  convertThenFormat,
  formatDateTimezon,
} from "../../common-data/Converts";
import { commonPostPutApi } from "../../../redux/reducers/commonApiSlice";
import { fetchMemberSubscriptionData } from "../../../redux/plans-reducers/plansSlice";
import MemberVerifiedRegistration from "../../common-components/custom-comps/MemberVerifiedRegistration";
import "../../../styles/transfer/transfer-sections.css";

const validateForm = (formValues) => {
  let errors = {};
  const mobileRegex = /^\d{10,}$/;

  if (!formValues.memberMobile) {
    errors.memberMobile = "Source Member Number Required!";
  } else if (!mobileRegex.test(formValues.memberMobile)) {
    errors.memberMobile = "Invalid mobile number!";
  }

  if (!formValues.selectedGym) {
    errors.selectedGym = "Gym Required!";
  }

  if (!formValues.staffMobile) {
    errors.staffMobile = "Target Member Number Required!";
  } else if (!mobileRegex.test(formValues.staffMobile)) {
    errors.staffMobile = "Invalid mobile number!";
  }

  if (formValues.memberMobile === formValues.staffMobile) {
    errors.staffMobile = "Cannot transfer to the same member!";
  }

  if (!formValues.processingFee) {
    errors.processingFee = "Processing fee is required!";
  } else if (Number(formValues.processingFee) < 0) {
    errors.processingFee = "Processing fee cannot be negative!";
  }

  if (formValues.isNewMember) {
    if (!formValues.memberName2) {
      errors.memberName2 = "Name is required!";
    }
    if (!formValues.staffEmail) {
      errors.staffEmail = "Email is required!";
    }
    if (!formValues.memberDOB2) {
      errors.memberDOB2 = "Date of birth is required!";
    }
    if (!formValues.memberGender2) {
      errors.memberGender2 = "Gender is required!";
    }
  }

  if (
    !formValues.paymentLinkFirstMember &&
    !formValues.paymentLinkSecondMember
  ) {
    errors.paymentLinkSelection =
      "Please select who to send the payment link to";
  }

  return errors;
};

const MembershipTransferForm = ({ userInfoData, closeModal, callFunction }) => {
  const dispatch = useDispatch();
  const { postPutLoading } = useSelector((state) => state.common);

  const [isFetchUser, setIsFetchUser] = useState(false);
  const [memberData, setMemberData] = useState(null);
  const [targetMemberData, setTargetMemberData] = useState(null);
  const [memStatus, setMemStatus] = useState({
    isShowForm: false,
    errorMessage: "",
  });

  const [expandedSections, setExpandedSections] = useState({
    regular: true,
    pt: true,
  });
  const [isMemberVerified, setIsMemberVerified] = useState(false);

  const iniFormValues = {
    selectedGym: "",
    memberMobile: "",
    memberName: "",
    memberEmail: "",
    memberInfo: "",

    staffMobile: "",
    memberName2: "",
    staffEmail: "",
    memberGender2: "",
    memberDOB2: "",
    member2Info: "",

    isNewMember: false,
    processingFee: "",
    isCustomFee: false,

    remarks: "",
    paymentLinkFirstMember: false,
    paymentLinkSecondMember: false,
    isMobileEmailVerified: false,
  };
  const [formValues, setFormValues] = useState(iniFormValues);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const formRef = useRef({});

  // Handle input changes
  const onChangeInput = (e) => {
    const { name, value, type, checked } = e.target;
    const file = type === "file" ? e.target.files[0] : null;

    setIsSubmit(false);

    if (name === "memberMobile" || name === "staffMobile") {
      const numbersOnly = value.replace(/\D/g, "");
      const limitedValue = numbersOnly.slice(0, 10);

      setFormValues((prevs) => ({
        ...prevs,
        [name]: limitedValue,
      }));
      return;
    }

    if (name === "processingFee" && Number(value) < 0) {
      return;
    }

    if (name === "isCustomFee") {
      setFormValues((prevs) => ({
        ...prevs,
        [name]: checked,
        processingFee: checked
          ? prevs.processingFee
          : prevs.memberInfo?.subscription_transfer_fee || 0,
      }));
    } else if (name === "processingFee") {
      const newValue = value;
      const isCustom = parseFloat(newValue) !== "";

      setFormValues((prevs) => ({
        ...prevs,
        [name]: newValue,
        isCustomFee: isCustom,
      }));
    } else if (name === "isNewMember") {
      setFormValues((prevs) => ({
        ...prevs,
        [name]: checked,
      }));
    } else {
      setFormValues((prevs) => ({
        ...prevs,
        [name]: type === "checkbox" ? checked : type === "file" ? file : value,
      }));
    }
  };

  // Add this new function to handle checkbox changes
  const handlePaymentLinkCheckbox = (memberType) => {
    setFormValues((prev) => ({
      ...prev,
      paymentLinkFirstMember: memberType === "first",
      paymentLinkSecondMember: memberType === "second",
    }));
  };

  // Toggle section expand/collapse
  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  const assignRef = (fieldName) => (el) => {
    if (el) {
      formRef.current[fieldName] = el;
    }
  };

  // Handle form submission
  const onSumitForm = (e) => {
    e.preventDefault();
    setFormErrors(validateForm(formValues, memberData, targetMemberData));
    setIsSubmit(true);
  };

  // Get subscription progress
  const getSubsProgress = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date();

    const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    const daysLeft = Math.ceil((end - today) / (1000 * 60 * 60 * 24));

    return {
      daysLeft: daysLeft > 0 ? daysLeft : 0,
      totalDays: totalDays,
    };
  };

  // Fetch member details
  function getMemberDetails(filters, isSource = true) {
    dispatch(
      fetchMemberSubscriptionData({
        filters: filters,
        token: userInfoData?.token,
      })
    ).then((result) => {
      if (isSource) {
        handleMemberResponse(result);
      } else {
        handleTargetMemberResponse(result);
      }
    });
  }

  // Handle source member API response
  function handleMemberResponse(result) {
    const response = result.payload;
    if (response?.status && response?.data?.length > 0) {
      const memberData = response.data;

      const hasActivePT = memberData.some(
        (item) =>
          item.type === "addon_pt" &&
          (item.subscription_status === "active" ||
            item.subscription_status === "draft")
      );

      if (hasActivePT) {
        setMemStatus({
          isShowForm: false,
          errorMessage:
            "This member has active PT plan so its membership cannot be transferred.",
        });
        setMemberData(null);
        setIsFetchUser(false);
        return;
      }

      // Check if there's at least one active regular membership
      const hasActiveRegularMembership = memberData.some(
        (item) =>
          item.type === "regular" && item.subscription_status === "active"
      );

      if (!hasActiveRegularMembership) {
        setMemStatus({
          isShowForm: false,
          errorMessage:
            "No active regular membership found. Only active regular memberships can be transferred.",
        });
        setMemberData(null);
        setIsFetchUser(false);
        return;
      }

      const {
        gym_name,
        gym_id,
        user_id,
        user_name,
        user_email,
        subscription_transfer_fee,
      } = memberData[0];

      setFormValues((prevs) => ({
        ...prevs,
        memberUserId: user_id,
        memberName: user_name,
        memberEmail: user_email,
        selectedGym: {
          label: gym_name,
          value: gym_id,
        },
        memberInfo: memberData[0],
        processingFee: subscription_transfer_fee || 0,
      }));

      setMemberData(memberData);
      setMemStatus({
        isShowForm: true,
        errorMessage: "",
      });
    } else {
      setMemStatus({
        isShowForm: false,
        errorMessage: "No active memberships found for this member.",
      });
    }
    setIsFetchUser(false);
  }

  // Handle target member API response
  function handleTargetMemberResponse(result) {
    const response = result.payload;
    setIsFetchUser(false);

    setFormErrors((prev) => ({
      ...prev,
      staffMobile:
        "Membership can only be transferred to new members, not existing ones.",
    }));

    const memberData = response.data;
    const { user_id, user_name, user_email } = memberData[0];

    setFormValues((prevs) => ({
      ...prevs,
      memberName2: user_name,
      staffEmail: user_email,
      isNewMember: false,
      member2Info: memberData[0],
    }));

    setTargetMemberData(memberData);

    setIsMemberVerified(true);
  }

  // Click handler for fetching source user details
  function onClickFetchUserDetails(e) {
    e.preventDefault();

    if (!formValues.memberMobile) {
      setMemberData(null);
      setMemStatus({
        isShowForm: false,
        errorMessage: "Please enter a valid member number",
      });

      return;
    }

    if (formValues.memberMobile && !formErrors.memberMobile) {
      const filters = {
        mobile_number: formValues.memberMobile,
        subscription_status: "active",
        // status: "active",
      };

      setIsFetchUser(true);
      getMemberDetails(filters, true);
    }
  }

  // Clear form
  function onClearForm(e) {
    e && e.preventDefault();

    const handleConfirm = () => {
      setFormValues(iniFormValues);
      setIsSubmit(false);
      setIsFetchUser(false);
      setMemberData(null);
      setTargetMemberData(null);
      setMemStatus({
        isShowForm: false,
        errorMessage: "",
      });

      setIsMemberVerified(false);
    };

    showConfirmAlert(handleConfirm, `You want to clear the form?`);
  }

  // Submit form to API
  const handleSubmitForm = async (apiPayload) => {
    try {
      const method = "POST";
      const endpoint = "/gym/transfer";

      const result = await dispatch(
        commonPostPutApi({
          method,
          endpoint,
          payload: apiPayload,
          token: userInfoData?.token,
        })
      );

      if (result?.payload?.status) {
        setIsSubmit(false);
        setFormValues(iniFormValues);
        setMemberData(null);
        setTargetMemberData(null);
        setMemStatus({
          isShowForm: false,
          errorMessage: "",
        });

        callFunction();
        closeModal();
        toast.success(
          result?.payload?.message ||
            "Membership transfer initiated successfully!"
        );
      } else {
        const errorMessage =
          result?.payload?.data ||
          result?.payload?.message ||
          result?.error?.message;
        toast.error(errorMessage || "Something Went Wrong!");
      }
    } catch (error) {
      console.error("Error processing membership transfer:", error);
      toast.error("Failed to process membership transfer");
    }
  };

  // Process form submission when validation passes
  useEffect(() => {
    if (isSubmit && Object.keys(formErrors)?.length === 0) {
      const payload = {
        user_from: formValues.memberInfo?.user_id || "",
        user_to: formValues.member2Info?.user_id || "",
        transfered_type: "subscription",
        gym_from: formValues.selectedGym?.value || "",
        gym_to: formValues.selectedGym?.value || "",
        transfered_fee: formValues.processingFee || 0,
        payment_by: formValues.paymentLinkFirstMember ? "self" : "receiver",
        is_custom:
          formValues.isCustomFee &&
          Number(formValues.processingFee || 0) !==
            Number(formValues.memberInfo?.subscription_transfer_fee || 0)
            ? 1
            : 0,
      };

      const handleConfirm = async () => {
        handleSubmitForm(payload);
      };

      showConfirmAlert(
        handleConfirm,
        `Are you sure you want to proceed with this membership transfer?`,
        "Edit Form",
        "Submit Form"
      );
    }
  }, [isSubmit, formErrors]);

  console.log("formErrors=> ", formErrors);

  return (
    <div className="form-container transfer-form-container">
      <form className="formContainer mb150">
        <div className="section-details">
          <div className="section-head">
            <h3>first member (transfer from)</h3>
          </div>

          {/* Source Member Section */}
          <div className="formInputsGrid twoGridSystem input-with-btn">
            <div className="formInputBox">
              <label htmlFor="memberMobile" className={`formInputLabel`}>
                mobile number <span className="required">*</span>
              </label>

              <input
                type="number"
                placeholder="Enter Mobile Number"
                className={`formInput ${
                  formErrors?.memberMobile ? "formError" : ""
                }`}
                name="memberMobile"
                value={formValues.memberMobile}
                onChange={onChangeInput}
              />

              <span className="error-label">
                {formErrors?.memberMobile || ""}
              </span>

              {!memStatus.isShowForm && memStatus.errorMessage && (
                <span className="info">{memStatus.errorMessage || ""}</span>
              )}
            </div>

            <div className="formInputButtons">
              <Button
                type="button"
                className="saveBtn"
                variant="danger"
                onClick={onClickFetchUserDetails}
                disabled={memberData || formErrors.memberMobile}>
                {isFetchUser ? <Loader /> : "Fetch Member Details"}
              </Button>
              {!isFetchUser && (
                <Button
                  type="button"
                  className="cancelBtn"
                  variant="outline-dark"
                  onClick={onClearForm}>
                  Clear
                </Button>
              )}
            </div>
          </div>

          {memStatus.isShowForm && (
            <div className="formInputsGrid twoGridSystem">
              <div className="formInputBox">
                <label htmlFor="memberName" className={`formInputLabel`}>
                  Full Name
                </label>

                <input
                  type="text"
                  placeholder="Enter Full Name"
                  className={`formInput ${
                    formErrors?.memberName ? "formError" : ""
                  }`}
                  name="memberName"
                  value={formValues.memberName}
                  disabled
                />
              </div>

              <div className="formInputBox">
                <label htmlFor="memberEmail" className={`formInputLabel`}>
                  email address
                </label>

                <input
                  type="email"
                  placeholder="Enter Email Address"
                  className={`formInput ${
                    formErrors?.memberEmail ? "formError" : ""
                  }`}
                  name="memberEmail"
                  value={formValues.memberEmail}
                  disabled
                />
              </div>

              <div className="formInputBox span2">
                <label htmlFor="selectedGym" className={`formInputLabel`}>
                  Gym Name
                </label>

                <input
                  type="text"
                  placeholder="Gym Name"
                  className={`formInput ${
                    formErrors?.selectedGym ? "formError" : ""
                  }`}
                  name="selectedGym"
                  value={formValues.selectedGym?.label}
                  disabled
                />
              </div>
            </div>
          )}
        </div>

        {memStatus.isShowForm && (
          <>
            <div className="section-details">
              <div className="section-head">
                <h3>second member (transfer to)</h3>
              </div>

              <div className="formInputsGrid twoGridSystem">
                <MemberVerifiedRegistration
                  isFromMembershipTransfer={true}
                  userInfoData={userInfoData}
                  formValues={formValues}
                  setFormValues={setFormValues}
                  formErrors={formErrors}
                  setFormErrors={setFormErrors}
                  isMemberVerified={isMemberVerified}
                  setIsMemberVerified={setIsMemberVerified}
                  assignRef={assignRef}
                />
              </div>
            </div>

            {/* Membership Details Section */}
            {memberData && isMemberVerified && (
              <>
                {/* Processing Fee Section - Placed after trainer assignment */}
                <div className="formInputsGrid twoGridSystem">
                  <div className="processing-fee-section span3">
                    <div className="formInputBox">
                      <label
                        htmlFor="processingFee"
                        className={`formInputLabel`}>
                        Processing Fee <span className="required">*</span>
                      </label>

                      <div className="processing-fee-wrapper">
                        <input
                          type="number"
                          placeholder="Enter Amount"
                          className={`formInput ${
                            formErrors?.processingFee ? "formError" : ""
                          }`}
                          name="processingFee"
                          value={formValues.processingFee}
                          onChange={onChangeInput}
                          disabled={!formValues.isCustomFee}
                          min="0"
                        />

                        <div className="manual-fee-toggle">
                          <input
                            type="checkbox"
                            id="isCustomFee"
                            name="isCustomFee"
                            checked={formValues.isCustomFee}
                            onChange={onChangeInput}
                            className="custom-checkbox"
                          />
                          <label
                            htmlFor="isCustomFee"
                            className="custom-checkbox-label">
                            Manual Fee
                          </label>
                        </div>
                      </div>

                      <span className="error-label">
                        {formErrors?.processingFee || ""}
                      </span>
                    </div>

                    {formValues.isCustomFee && (
                      <div className="alert-box warning">
                        <div className="alert-icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                          </svg>
                        </div>
                        <div className="alert-content">
                          <p>
                            Important: Manual membership transfer fee requires
                            administrator approval. After submitting this form,
                            the transfer request will be placed in a pending
                            state. The membership transfer will only be
                            processed and finalized after an administrator
                            reviews and approves the custom fee amount. This
                            additional step helps ensure all fee adjustments are
                            properly authorized.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Payment Link Send To Dropdown */}

                  <div className="formInputBox checkbox-wrapper">
                    <label className={`formInputLabel`}>
                      Send Payment Link To <span className="required">*</span>
                    </label>

                    <div className="flex-box">
                      <label className="checkboxLabel">
                        <input
                          type="checkbox"
                          name="paymentLinkFirstMember"
                          checked={formValues.paymentLinkFirstMember}
                          onChange={() => handlePaymentLinkCheckbox("first")}
                        />
                        {formValues.memberName || "First Member"}
                      </label>

                      <label className="checkboxLabel">
                        <input
                          type="checkbox"
                          name="paymentLinkSecondMember"
                          checked={formValues.paymentLinkSecondMember}
                          onChange={() => handlePaymentLinkCheckbox("second")}
                        />
                        {formValues.memberName2 || "Second Member"}
                      </label>
                    </div>

                    <span className="error-label">
                      {formErrors?.paymentLinkSelection || ""}
                    </span>
                  </div>
                </div>

                <div className="memberships-container">
                  {/* Regular Memberships */}
                  <div
                    className="membership-section"
                    style={{ marginBottom: "16px" }}>
                    <div
                      className={`section-header ${
                        expandedSections.regular ? "expanded" : ""
                      } ${formErrors.selectedPlans ? "error" : ""}`}
                      onClick={() => toggleSection("regular")}>
                      <div className="section-title">
                        Regular Memberships
                        <span className="count">
                          {/* {selectedRegularPlans.length}/ */}
                          {
                            memberData.filter(
                              (item) =>
                                item.type === "regular" &&
                                (item.subscription_status === "active" ||
                                  item.subscription_status === "draft")
                            ).length
                          }{" "}
                          Plan(s)
                        </span>
                        {formErrors.selectedPlans && (
                          <span className="error-message">
                            Select at least one plan to transfer
                          </span>
                        )}
                      </div>
                    </div>

                    <div
                      className={`plans-grid ${
                        expandedSections.regular ? "expanded" : ""
                      }`}>
                      {memberData &&
                      memberData.some(
                        (item) =>
                          item.type === "regular" &&
                          moment(item.expire_date)
                            .tz("Asia/Kolkata")
                            .isAfter(moment().tz("Asia/Kolkata"))
                      ) ? (
                        memberData
                          .filter(
                            (item) =>
                              item.type === "regular" &&
                              moment(item.expire_date)
                                .tz("Asia/Kolkata")
                                .isAfter(moment().tz("Asia/Kolkata"))
                          )
                          .map((item, index) => {
                            const {
                              plan_name,
                              price,
                              start_date,
                              expire_date,
                              uid,
                              duration,
                            } = item || {};

                            const startDate = start_date
                              ? formatDateTimezon(start_date).split(" ")[0]
                              : "-";
                            const endDate = expire_date
                              ? formatDateTimezon(expire_date).split(" ")[0]
                              : "-";

                            // Get days left from the existing function
                            const { daysLeft } = getSubsProgress(
                              start_date,
                              expire_date
                            );

                            const planDuration = parseInt(duration) || 0;
                            const halfDuration = planDuration / 2;

                            const isPlanDurationTooShort = planDuration <= 30;
                            const isTooFewDaysLeft = daysLeft <= halfDuration;
                            const isDisabled =
                              isPlanDurationTooShort || isTooFewDaysLeft;

                            return (
                              <div
                                className={`plan-card isDisabled ${
                                  isDisabled ? "disabled" : ""
                                }`}
                                key={index}>
                                <div className="plan-header">
                                  <div>
                                    <h3 className="plan-name">
                                      {plan_name || "Regular Plan"}
                                    </h3>
                                    <div className="plan-type">
                                      Regular Membership
                                    </div>
                                  </div>
                                  <span className="status">
                                    {item.subscription_status === "active"
                                      ? "ACTIVE"
                                      : "DRAFT"}
                                  </span>
                                </div>

                                <div className="plan-body">
                                  <div className="plan-detail">
                                    <div className="detail-label">Price:</div>
                                    <div className="detail-value price">
                                      ₹{convertThenFormat(price || 0)}
                                    </div>
                                  </div>

                                  <div className="plan-detail">
                                    <div className="detail-label">
                                      Duration:
                                    </div>
                                    <div className="detail-value">
                                      {startDate} - {endDate} ({duration} days)
                                    </div>
                                  </div>

                                  <div className="plan-detail">
                                    <div className="detail-label">
                                      Remaining:{" "}
                                    </div>
                                    <div className="detail-value">
                                      <span className="days-left">
                                        {daysLeft} days
                                      </span>
                                    </div>
                                  </div>

                                  {/* {isDisabled && (
                                    <div className="transfer-disabled-message">
                                      {disabledMessage}
                                    </div>
                                  )} */}

                                  {/* <button
                                    type="button"
                                    className={`transfer-btn ${
                                      isSelected ? "selected" : ""
                                    } ${isDisabled ? "disabled" : ""}`}
                                    onClick={() =>
                                      !isDisabled &&
                                      handleSelectRegularPlan(uid)
                                    }
                                    disabled={isDisabled}>
                                    {isSelected ? (
                                      <>
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round">
                                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                        </svg>
                                        Selected for Transfer
                                      </>
                                    ) : (
                                      <>
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round">
                                          <path d="M7 16V4M7 4L3 8M7 4L11 8"></path>
                                          <path d="M17 8v12M17 20l4-4M17 20l-4-4"></path>
                                        </svg>
                                        {isDisabled
                                          ? "Cannot Transfer"
                                          : "Select for Transfer"}
                                      </>
                                    )}
                                  </button> */}
                                </div>
                              </div>
                            );
                          })
                      ) : (
                        <div className="empty-message">
                          No transferable memberships
                        </div>
                      )}
                    </div>
                  </div>

                  {/* PT Memberships */}
                  <>
                    {/* <div className="membership-section">
                      <div
                        className={`section-header ${
                          expandedSections.pt ? "expanded" : ""
                        }`}
                        onClick={() => toggleSection("pt")}>
                        <div className="section-title">
                          Personal Training
                          <span className="count">
                            {selectedPTPlans.length}/
                            {
                              memberData.filter(
                                (item) =>
                                  item.type === "addon_pt" &&
                                  item.subscription_status === "active"
                              ).length
                            }
                          </span>
                        </div>
                      </div>

                      <div
                        className={`plans-grid ${
                          expandedSections.pt ? "expanded" : ""
                        }`}>
                        {memberData.some(
                          (item) =>
                            item.type === "addon_pt" &&
                            item.subscription_status === "active"
                        ) ? (
                          memberData
                            .filter(
                              (item) =>
                                item.type === "addon_pt" &&
                                item.subscription_status === "active"
                            )
                            .map((item, index) => {
                              const {
                                addon_name,
                                price,
                                start_date,
                                expire_date,
                                uid,
                                trainer_name,
                              } = item || {};

                              const startDate = start_date
                                ? formatDateTimezon(start_date).split(" ")[0]
                                : "-";
                              const endDate = expire_date
                                ? formatDateTimezon(expire_date).split(" ")[0]
                                : "-";
                              const daysLeft = getSubsProgress(
                                start_date,
                                expire_date
                              ).daysLeft;
                              const isSelected = selectedPTPlans.includes(uid);

                              return (
                                <div
                                  className={`plan-card ${
                                    isSelected ? "selected" : ""
                                  }`}
                                  key={index}>
                                  <div className="plan-header">
                                    <div>
                                      <h3 className="plan-name">
                                        {addon_name || "PT Sessions"}
                                      </h3>
                                      <div className="plan-type">
                                        Personal Training
                                      </div>
                                    </div>
                                    <span className="status">ACTIVE</span>
                                  </div>

                                  <div className="plan-body">
                                    <div className="trainer-box">
                                      <div className="trainer-avatar">
                                        {(trainer_name || "T")[0].toUpperCase()}
                                      </div>
                                      <div className="trainer-info">
                                        <div className="trainer-name">
                                          {trainer_name || "Not assigned"}
                                        </div>
                                        <div className="trainer-title">
                                          Current Trainer
                                        </div>
                                      </div>
                                    </div>

                                    <div className="plan-detail">
                                      <div className="detail-label">Price:</div>
                                      <div className="detail-value price">
                                        ₹{convertThenFormat(price || 0)}
                                      </div>
                                    </div>

                                    <div className="plan-detail">
                                      <div className="detail-label">
                                        Duration:
                                      </div>
                                      <div className="detail-value">
                                        {startDate} - {endDate}
                                      </div>
                                    </div>

                                    <div className="plan-detail">
                                      <div className="detail-label">
                                        Remaining:
                                      </div>
                                      <div className="detail-value">
                                        <span className="days-left">
                                          {daysLeft} days
                                        </span>
                                      </div>
                                    </div>

                                    <button
                                      type="button"
                                      className={`transfer-btn ${
                                        isSelected ? "selected" : ""
                                      }`}
                                      onClick={() => handleSelectPTPlan(uid)}>
                                      {isSelected ? (
                                        <>
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                          </svg>
                                          Selected for Transfer
                                        </>
                                      ) : (
                                        <>
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round">
                                            <path d="M7 16V4M7 4L3 8M7 4L11 8"></path>
                                            <path d="M17 8v12M17 20l4-4M17 20l-4-4"></path>
                                          </svg>
                                          Select for Transfer
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              );
                            })
                        ) : (
                          <div className="empty-message">
                            No active PT memberships
                          </div>
                        )}
                      </div>
                    </div> */}
                  </>
                </div>

                {/* Trainer and Slot Selection - Only visible when PT plan is selected */}
                <>
                  {/* {selectedPTPlans.length > 0 && (
                    <div className="formInputsGrid twoGridSystem">
                      <div className="formInputBox">
                        <label htmlFor="trainer" className={`formInputLabel`}>
                          Select New Trainer <span className="required">*</span>
                        </label>

                        <div
                          className={`commonWrapper ${
                            formErrors.selectedTrainer ? "formError" : ""
                          }`}>
                          <SelectDropdown
                            options={allGymTrainers}
                            selectedOption={formValues.selectedTrainer}
                            setSelectedOption={(selectedValue) =>
                              onChangeDropdown("selectedTrainer", selectedValue)
                            }
                            name="Trainer"
                            isDisable={trainerLoading}
                            placeholder="Select a trainer"
                          />
                        </div>

                        <span className="error-label">
                          {formErrors?.selectedTrainer || ""}
                        </span>
                      </div>

                      <div className="formInputBox">
                        <label htmlFor="timeSlot" className={`formInputLabel`}>
                          Time Slot <span className="required">*</span>
                        </label>

                        <div
                          className={`commonWrapper ${
                            formErrors.selectedSlotIndex ? "formError" : ""
                          }`}>
                          <SelectDropdown
                            options={trainerShifts}
                            selectedOption={formValues.selectedSlotIndex}
                            setSelectedOption={(selectedValue) =>
                              onChangeDropdown(
                                "selectedSlotIndex",
                                selectedValue
                              )
                            }
                            name="Slot"
                            isDisable={
                              trainerSlotLoading ||
                              !formValues.selectedTrainer?.value
                            }
                          />
                        </div>

                        <span className="error-label">
                          {formErrors?.selectedSlotIndex || ""}
                        </span>
                      </div>
                    </div>
                  )} */}
                </>

                <div
                  className="formButtons"
                  style={{ justifyContent: "flex-end" }}>
                  <Button
                    type="button"
                    className="saveBtn"
                    variant="danger"
                    onClick={onSumitForm}
                    disabled={!memStatus.isShowForm}>
                    {postPutLoading ? <Loader /> : "Submit"}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </form>
    </div>
  );
};

export default MembershipTransferForm;
